import {
  PropsWithChildren,
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { View } from 'react-native';
import RNModal from 'react-native-modal';
import { SafeAreaView } from 'react-native-safe-area-context';
import { Text } from '@/components/Text';
import { Modal as TherapistModal } from '@/components/ui/therapist/Modal';
import { PrimaryButton } from '@/components/Button';
import { allowNativeDriver } from '@/utils/allowNativeDriver';
import {
  DialogContextValue,
  DialogId,
  DialogOptions,
  DialogProps,
  ShowDialogFn,
} from './types';
import { isPatientApp } from '@/config/constants';
import { Button } from '@/components/ui/therapist/Button';
import { Modal as PatientModal } from '@/components/ui/patient/Modal';
import { createDialogButtons, resolveCancelPress } from './createDialogButtons';
import { styles } from './styles';

export const DialogContext = createContext<DialogContextValue | null>(null);
export function DialogProvider({ children }: PropsWithChildren) {
  const [dialogs, setDialogs] = useState<DialogOptions[]>([]);
  const idCounter = useRef(0);

  // Remove a specific dialog by id; with no id, pop the top (the visible one).
  // Stable identity: consumers list these in effect deps, so an unstable ref
  // would re-fire those effects on every provider render (infinite loop).
  const removeDialog = useCallback((id?: DialogId) => {
    setDialogs((p) =>
      id == null ? p.slice(0, -1) : p.filter((d) => d.id !== id),
    );
  }, []);

  const removeAllDialogs = useCallback(() => {
    setDialogs([]);
  }, []);

  const showDialog: ShowDialogFn = useCallback((options: DialogOptions) => {
    const id = ++idCounter.current;
    setDialogs((p) => [...p, { ...options, id }]);
    return id;
  }, []);

  // Replace a still-mounted dialog's options in place (keeps its id/position).
  const updateDialog = useCallback((id: DialogId, options: DialogOptions) => {
    setDialogs((p) => p.map((d) => (d.id === id ? { ...options, id } : d)));
  }, []);

  const currentDialogOptions = dialogs[dialogs.length - 1];
  const openDialogIds = useMemo(
    () =>
      dialogs
        .map((d) => d.id)
        .filter((id): id is DialogId => typeof id === 'number'),
    [dialogs],
  );

  const contextValue = useMemo(
    () => ({
      showDialog,
      removeDialog,
      removeAllDialogs,
      updateDialog,
      openDialogIds,
    }),
    [showDialog, removeDialog, removeAllDialogs, updateDialog, openDialogIds],
  );

  return (
    <DialogContext.Provider value={contextValue}>
      {children}
      {!!currentDialogOptions && (
        <Dialog
          key={currentDialogOptions.id}
          isVisible
          options={currentDialogOptions}
          onClose={removeDialog}
        />
      )}
    </DialogContext.Provider>
  );
}

function Dialog({ isVisible, onClose, ...props }: DialogProps) {
  const [options, setOptions] = useState(props.options);

  useEffect(() => {
    setOptions(props.options);
  }, [props.options]);

  const {
    content,
    contentComponent,
    title,
    danger,
    headingComponent,
    showCancelX,
    cancelOnBackdropPress,
    wide,
    fullscreen,
  } = options;

  const { confirm, cancel } = createDialogButtons(
    options,
    onClose,
    isPatientApp,
  );
  // Both the corner X and the cancel button share this handler, so dismissing
  // via the X runs the same cancelAction side-effects as the cancel button.
  const onCancelPress = resolveCancelPress(options, onClose);

  // Fullscreen shell (both apps): content fills the screen, single confirm
  // button. For content that can't live in the bounded modal — e.g. a chart
  // webview that measures its own height.
  if (fullscreen) {
    return (
      <RNModal
        isVisible={isVisible}
        style={styles.fullscreenModal}
        statusBarTranslucent
        useNativeDriver={allowNativeDriver()}
        useNativeDriverForBackdrop={allowNativeDriver()}
        hideModalContentWhileAnimating
      >
        <SafeAreaView style={styles.fullscreenSafeArea}>
          <View style={styles.fullscreenContent}>
            {contentComponent ? (
              contentComponent({ setOptions })
            ) : content ? (
              <Text style={styles.contentText}>{content}</Text>
            ) : null}
          </View>
          {confirm ? (
            <View style={styles.fullscreenButtonRow}>
              <PrimaryButton
                testID={confirm.testID}
                look={confirm.danger ? 'danger' : 'default'}
                label={confirm.label}
                disabled={confirm.disabled}
                onPress={confirm.onPress}
              />
            </View>
          ) : null}
        </SafeAreaView>
      </RNModal>
    );
  }

  if (!isPatientApp) {
    return (
      <TherapistModal
        title={title ?? ''}
        isVisible={isVisible}
        wide={wide}
        onClose={showCancelX ? onCancelPress : undefined}
        cancelActionButton={
          cancel ? (
            <Button
              testID={cancel.testID}
              look="secondary"
              onPress={cancel.onPress}
              label={cancel.label}
            />
          ) : undefined
        }
        confirmActionButton={
          confirm ? (
            <Button
              testID={confirm.testID}
              look={confirm.danger ? 'danger' : 'primary'}
              onPress={confirm.onPress}
              label={confirm.label}
              disabled={confirm.disabled}
            />
          ) : undefined
        }
      >
        {content ? (
          <Text style={styles.contentText}>{content}</Text>
        ) : contentComponent ? (
          contentComponent({ setOptions })
        ) : null}
      </TherapistModal>
    );
  }

  const patientContent = contentComponent ? (
    contentComponent({ setOptions })
  ) : content ? (
    <View style={styles.contentPadding}>
      <Text style={styles.contentText}>{content}</Text>
    </View>
  ) : null;

  return (
    <PatientModal
      title={title}
      isVisible={isVisible}
      wide={wide}
      onClose={onCancelPress}
      danger={danger}
      headingComponent={headingComponent}
      showCancelX={showCancelX}
      cancelOnBackdropPress={cancelOnBackdropPress}
      boundContent={!!contentComponent}
      confirmActionButton={
        confirm ? (
          <PrimaryButton
            testID={confirm.testID}
            look={confirm.danger ? 'danger' : 'default'}
            label={confirm.label}
            disabled={confirm.disabled}
            onPress={confirm.onPress}
          />
        ) : undefined
      }
      cancelActionButton={
        cancel ? (
          <PrimaryButton
            testID={cancel.testID}
            look="neutral"
            label={cancel.label}
            onPress={cancel.onPress}
          />
        ) : undefined
      }
    >
      {patientContent}
    </PatientModal>
  );
}
